import { Button, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult, UseQueryResult } from 'react-query';

import { Dialog, DialogProps } from 'components';
import { usePrevious } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { P2PProviderRequest } from 'types';

import { P2PProvidersRequestsTable } from './P2PProvidersRequestsTable';

type Props = {
  queryResult: UseQueryResult<P2PProviderRequest[]>;
  orderId: string;
  pollMutation?: UseMutationResult<void, AxiosError, { orderId: string }>;
} & DialogProps;

export const P2PProvidersRequestsDialog: React.FC<Props> = ({
  queryResult,
  orderId,
  pollMutation,
  open,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.p2p_providers_requests',
  });
  const [formatJson, setFormatJson] = useState(false);
  const prevOpen = usePrevious(open);

  const toggleSetFormatJson = useCallback(() => {
    setFormatJson((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!prevOpen && open) {
      queryResult.refetch();
    }
  }, [prevOpen, open, queryResult]);

  return (
    <Dialog
      title={t('title')}
      open={open}
      modal
      maxWidth="xl"
      onClose={onClose}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <FormGroup>
          <FormControlLabel
            control={
              <Switch checked={formatJson} onChange={toggleSetFormatJson} />
            }
            label={t('dialog.format_json')}
          />
        </FormGroup>
        {pollMutation && (
          <Button
            variant="outlined"
            size="small"
            disabled={pollMutation.isLoading}
            onClick={() => pollMutation.mutate({ orderId })}
          >
            {t('dialog.poll_status')}
          </Button>
        )}
      </div>
      <P2PProvidersRequestsTable
        queryResult={queryResult}
        formatJson={formatJson}
      />
    </Dialog>
  );
};
